import { useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { useRouter } from "../lib/router.js";
import { useSession, type SessionUser } from "../lib/session.js";
import { RoleBadge } from "../components/ui.js";
import { NotificationSettings } from "../components/NotificationSettings.js";

export function SettingsPage() {
  const { user, setUser, logout } = useSession();
  const { navigate } = useRouter();

  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [profileMsg, setProfileMsg] = useState<string | null>(null);
  const [profileErr, setProfileErr] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwMsg, setPwMsg] = useState<string | null>(null);
  const [pwErr, setPwErr] = useState<string | null>(null);
  const [savingPw, setSavingPw] = useState(false);

  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  async function exportData() {
    setExportErr(null);
    setExporting(true);
    try {
      const res = await fetch("/api/account/export", { credentials: "include" });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clovaform-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setExportErr("Could not export your data. Try again.");
    } finally {
      setExporting(false);
    }
  }

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    setProfileMsg(null);
    setProfileErr(null);
    setSavingProfile(true);
    try {
      const result = await api.patch<{ user: SessionUser }>("/api/account/profile", {
        displayName: displayName || undefined,
        email
      });
      setUser(result.user);
      setProfileMsg("Profile updated.");
    } catch (err) {
      setProfileErr(err instanceof ApiError ? err.message : "Could not save.");
    } finally {
      setSavingProfile(false);
    }
  }

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwMsg(null);
    setPwErr(null);
    if (newPassword !== confirmPassword) {
      setPwErr("The new passwords do not match.");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/api/account/password", { currentPassword, newPassword });
      setPwMsg("Password changed. Other sessions have been signed out.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setPwErr(err instanceof ApiError ? err.message : "Could not change password.");
    } finally {
      setSavingPw(false);
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1>Settings</h1>
        <p className="subtitle">Your account and preferences.</p>
      </div>

      <div className="stack" style={{ maxWidth: 560 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Account</span>
            {user && <RoleBadge role={user.role} />}
          </div>
          <div className="table-card">
            <div className="tc-row">
              <span className="tc-label">Username</span>
              <span className="mono">{user?.username}</span>
            </div>
          </div>

          {profileErr && <div className="alert alert-danger mt-4">{profileErr}</div>}
          {profileMsg && <div className="alert alert-success mt-4">{profileMsg}</div>}

          <form onSubmit={saveProfile} className="mt-4">
            <div className="field">
              <label htmlFor="displayName">Display name</label>
              <input
                id="displayName"
                className="input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                className="input"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <button className="btn btn-primary" type="submit" disabled={savingProfile}>
              {savingProfile ? "Saving…" : "Save profile"}
            </button>
          </form>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Change password</span>
          </div>
          {pwErr && <div className="alert alert-danger">{pwErr}</div>}
          {pwMsg && <div className="alert alert-success">{pwMsg}</div>}
          <form onSubmit={changePassword}>
            <div className="field">
              <label htmlFor="currentPassword">Current password</label>
              <input
                id="currentPassword"
                className="input"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <div className="field">
              <label htmlFor="newPassword">New password</label>
              <input
                id="newPassword"
                className="input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <span className="hint">At least 10 characters.</span>
            </div>
            <div className="field">
              <label htmlFor="confirmPassword">Confirm new password</label>
              <input
                id="confirmPassword"
                className="input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <button className="btn btn-primary" type="submit" disabled={savingPw}>
              {savingPw ? "Updating…" : "Change password"}
            </button>
          </form>
        </div>

        <NotificationSettings />

        <div className="card">
          <div className="card-header">
            <span className="card-title">Your data</span>
          </div>
          <p className="muted text-sm">
            Download everything ClovaForm holds about you — profile, logs, plans, progress, and journal — as a single JSON file. It never leaves your control.
          </p>
          {exportErr && <div className="alert alert-danger mt-4">{exportErr}</div>}
          <button className="btn mt-4" onClick={exportData} disabled={exporting}>
            {exporting ? "Preparing…" : "Export my data"}
          </button>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Sessions</span>
          </div>
          <p className="muted text-sm">
            Sign out of ClovaForm on every device, including this one.
          </p>
          <button
            className="btn mt-4"
            onClick={async () => {
              await api.post("/api/account/sign-out-everywhere").catch(() => undefined);
              await logout();
              navigate("/login", { replace: true });
            }}
          >
            Sign out everywhere
          </button>
        </div>
      </div>
    </div>
  );
}
